// Provides a context for accessing the Asset API.
// Currently supports getting and putting a single asset, but this will be extended to encapsulate
// CRUD apis for assets and collections of assets more broadly.
// This also provides a place to do caching (currently handled by SWR) and undo/redo support.

import useApi from 'hooks/use-api'
import { useHashParam } from 'hooks/use-hash'
import { AssetData } from 'lib/assets/asset'
import { ASSET_REGISTRY, RegistryEntry } from 'lib/assets/registry'
import { getDefaults } from 'lib/rich'
import React, { ReactNode, createContext, useContext } from 'react'
import useSWR from 'swr'
import { useCurrentUser } from './current-user-context'

type ContextType = {
  getAsset: (assetType: string, uuid: string, asof?: Date) => Promise<AssetData>
  putAsset: (asset: AssetData) => Promise<AssetData>
}

const Context = createContext<ContextType | undefined>(undefined)

// Super simple cache test. This needs to get expanded to keep track of request time and we'd need a way to check for updates on the server.
const asset_cache = {}
const use_cache = false

// TODO: Add undo/redo support

type Props = {
  children: ReactNode
  asOfDate?: Date
}

export const AssetProvider: React.FC<Props> = ({ children, asOfDate }) => {
  const api = useApi()
  const currentUser = useCurrentUser()

  const updateCache = (asset: AssetData) => {
    // Save to cache
    if (use_cache) {
      asset_cache[asset.uuid] = asset
    }
  }

  const getAsset = async (assetType: string, uuid: string, asof?: Date) => {
    const typeEntry: RegistryEntry = ASSET_REGISTRY[assetType]
    if (!typeEntry) {
      return // Invalid assetType (may be temporary)
    }

    // Cache lookup
    if (use_cache) {
      const cached = asset_cache[uuid]
      if (cached) {
        return cached
      }
    }

    let url = `/api/assets/${assetType}/${uuid}`
    if (asof) {
      url += `?asof=${encodeURIComponent(asof.toUTCString())}`
    }

    const data = await api(url)

    if (!data) {
      throw new Error('No data received')
    }
    if (!data?.asset) {
      throw new Error('No asset received')
    }

    const asset: AssetData = data.asset as AssetData

    // TODO: Migrate asset if not at latest version

    // Fill in default values for any missing fields
    const metric = (asset?.data.metric || currentUser.defaultUnit) == 'mm'
    const defaultValues = getDefaults<any>(typeEntry.fieldSpecs, metric)
    asset.data = { ...defaultValues, ...asset.data }

    updateCache(asset)
    return asset
  }

  const putAsset = async (asset: AssetData) => {
    updateCache(asset)

    const res = await api(
      `/api/assets/${asset.type}/${asset.uuid}`,
      'PUT',
      asset,
    )
    return res as AssetData
  }

  return (
    <Context.Provider value={{ getAsset, putAsset }}>
      {children}
    </Context.Provider>
  )
}

export const useAssetContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useAssetContext must be used within an AssetProvider')
  }
  return context
}

/**
 * Custom hook to fetch and update a single asset.
 *
 * @param assetType - The type of the asset.
 * @param uuid - Unique identifier for the asset.
 * @param asOfDate - An optional Date override; if null, the hook will extract the value from the URL hash.
 * @returns An object containing { asset, error, isLoading, putAsset }.
 */
export const useSingleAsset = (
  assetType: string,
  uuid: string,
  asOfDate: Date | null = null,
  revalidateOnFocus: boolean = false,
) => {
  const hashAsOf = useHashParam('asof')
  if (!asOfDate && hashAsOf) {
    asOfDate = new Date(hashAsOf)
  }

  const { getAsset, putAsset } = useAssetContext()

  // Build the cache key. Only include the asof parameter if effectiveAsof is defined.
  let key = ''
  if (uuid && assetType) {
    key += `/api/assets/${assetType}/${uuid}`
    if (asOfDate) {
      key += `?asof=${asOfDate.toUTCString()}`
    }
  }

  const { data, error, isLoading, mutate } = useSWR(
    key,
    () => getAsset(assetType, uuid, asOfDate),
    { revalidateOnFocus },
  )

  // Wrap putAsset to update the cache with the latest asset data.
  const saveAsset = async (asset: AssetData) => {
    const updatedAsset = await putAsset(asset)
    mutate(updatedAsset, false)
    return updatedAsset
  }

  return { asset: data, error, isLoading, saveAsset }
}
